import { useCallback, useEffect, useMemo, useState } from 'react';

import GenericTable from 'components/display/Table/Table';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';

import { AccessMember, GerantWithoutAccess, getPersonneMoraleMembers } from '../api/personneMoraleAccessApi';
import { useCurrentAccess } from '../hooks/useCurrentAccess';
import { AddCollaboratorForm } from './team/form/AddCollaboratorForm';
import { MemberBriquesForm } from './team/form/MemberBriquesForm';
import { DeclareGerantPanel } from './team/DeclareGerantPanel';
import {
  buildBaseMemberMenuItems,
  memberGerantColumn,
  memberOwnerColumn,
  useMemberTeamActions,
} from './team/memberTeamShared';

/**
 * Page "Mon équipe" — gestion des collaborateurs d'une personne morale.
 * Visible uniquement pour les Owners (côté UI : on cache les actions sinon).
 */
export const TeamPage: React.FC = () => {
  const { access, canManageTeam, isReady } = useCurrentAccess();
  const { openPanel } = usePanel();

  const [members, setMembers] = useState<AccessMember[]>([]);
  const [gerantsWithoutAccess, setGerantsWithoutAccess] = useState<GerantWithoutAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<AccessMember | null>(null);

  const refresh = useCallback(async () => {
    if (!access) return;
    setLoading(true);
    try {
      const data = await getPersonneMoraleMembers(access.personneMoraleId);
      setMembers(data.collaborators);
      setGerantsWithoutAccess(data.gerantsWithoutAccess ?? []);
    } catch {
      toasterService.showToast({
        message: 'Erreur de chargement de l\'équipe.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setLoading(false);
    }
  }, [access]);

  useEffect(() => {
    if (isReady && access) refresh();
  }, [isReady, access, refresh]);

  const openAddForm = useCallback(() => {
    if (!access) return;
    openPanel(
      { title: 'Ajouter un collaborateur', size: Sizes.lg },
      <AddCollaboratorForm
        personneMoraleId={access.personneMoraleId}
        onSuccess={refresh}
      />,
    );
  }, [access, openPanel, refresh]);

  const { handleSetOwner, handlePromote, removeMember, handleRemoveGerant } = useMemberTeamActions(refresh);

  const openDeclareGerant = useCallback(
    (member?: AccessMember) => {
      if (!access) return;
      openPanel(
        { title: 'Déclarer le gérant du cabinet', size: Sizes.lg },
        <DeclareGerantPanel
          personneMoraleId={access.personneMoraleId}
          defaultAccessId={member?.accessId}
          onSuccess={refresh}
        />,
      );
    },
    [access, openPanel, refresh],
  );

  const handleRemove = useCallback(async () => {
    if (!memberToRemove) return;
    if (await removeMember(memberToRemove)) {
      setMemberToRemove(null);
    }
  }, [memberToRemove, removeMember]);

  const { createMenuItem } = useActionMenu();

  const openBriquesPanel = useCallback(
    (member: AccessMember) =>
      openPanel(
        { title: `Briques — ${member.displayName}`, size: Sizes.lg },
        <MemberBriquesForm
          accessId={member.accessId}
          memberName={member.displayName}
          onSuccess={refresh}
        />,
      ),
    [openPanel, refresh],
  );

  const createMenuItems = useCallback(
    (member: AccessMember) => [
      ...buildBaseMemberMenuItems(createMenuItem, member, {
        onPromote: handlePromote,
        onToggleOwner: handleSetOwner,
        onDeclareGerant: openDeclareGerant,
        onRemoveGerant: handleRemoveGerant,
      }),
      createMenuItem(
        'manage-briques',
        'Gérer les briques',
        () => openBriquesPanel(member),
        // L'auto-édition est refusée côté serveur (un Owner pourrait se couper
        // l'accès) — on désactive l'entrée pour soi-même.
        { icon: 'edit', disabled: member.userId === access?.abpUserId },
      ),
      createMenuItem(
        'remove',
        'Retirer',
        () => setMemberToRemove(member),
        { icon: 'trash', variant: 'danger' },
      ),
    ],
    [createMenuItem, handlePromote, handleSetOwner, handleRemoveGerant, openDeclareGerant, openBriquesPanel, access],
  );

  const tableConfig = useMemo(
    () => ({
      title: { label: access ? `Équipe — ${access.personneMoraleDesignation}` : 'Équipe' },
      data: members,
      loading,
      columns: [
        { key: 'displayName' as const, label: 'Nom', sortable: true },
        { key: 'email' as const, label: 'Email', sortable: true },
        memberOwnerColumn,
        memberGerantColumn,
        {
          key: 'dateAdhesion' as const,
          label: 'Adhésion',
          render: (_: any, row: AccessMember) =>
            new Date(row.dateAdhesion).toLocaleDateString(),
        },
      ],
      actions: canManageTeam
        ? {
            create: { label: 'Ajouter un collaborateur', onClick: openAddForm },
            row: [
              {
                label: 'moreMenu',
                render: (row: AccessMember, index: number) => (
                  <DropdownButton
                    key={index}
                    width='w-64'
                    anchor='bottom'
                    items={createMenuItems(row)}
                  />
                ),
              },
            ],
          }
        : undefined,
    }),
    [access, members, loading, canManageTeam, openAddForm, createMenuItems],
  );

  if (!isReady) return <SimpleDashboardContent>Chargement…</SimpleDashboardContent>;
  if (!access)
    return (
      <SimpleDashboardContent>Vous n'êtes rattaché à aucune personne morale.</SimpleDashboardContent>
    );

  return (
    <SimpleDashboardContent>
      {gerantsWithoutAccess.length > 0 && (
        <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
          <p className="font-medium">
            {gerantsWithoutAccess.length === 1
              ? `Le gérant ${gerantsWithoutAccess[0].displayName} n'a pas de compte.`
              : `${gerantsWithoutAccess.length} gérants du cabinet n'ont pas de compte.`}
          </p>
          <p className="mt-1">
            Il a été saisi à la création du cabinet. Pour qu'il puisse envoyer et signer les mandats
            depuis la plateforme, rattachez-le au compte qu'il utilise
            {canManageTeam ? ' via « Déclarer signataire des mandats » sur ce compte.' : '.'}
          </p>
        </div>
      )}

      <GenericTable config={tableConfig} />

      <ConfirmationOverlay
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemove}
        title="Retirer le collaborateur"
        description={
          memberToRemove
            ? `Confirmer le retrait de ${memberToRemove.displayName} ? Les ressources créées par cet utilisateur restent rattachées à la personne morale.`
            : ''
        }
        confirmLabel="Retirer"
        cancelLabel="Annuler"
        variant="danger"
      />
    </SimpleDashboardContent>
  );
};
